import "./PowerLines.css";
import React, { useMemo, useState, useCallback } from "react";
import { GeoJsonLayer, PathLayer } from "@deck.gl/layers";

const DEFAULT_COLOR = [255, 165, 0, 200];
const SELECTED_COLOR = [255, 255, 51, 255];

const normalizeValue = (value) =>
    String(value ?? "")
        .trim()
        .replace(/\s+/g, " ")
        .toLowerCase();

const getFeatureProps = (item) =>
    item?.properties ||
    item?.object?.properties ||
    item?.feature?.properties ||
    {};

const getVodValue = (item) => {
    const props = getFeatureProps(item);
    return props?.pripadnost_vodu;
};

const PowerLines = ({ wfsLayers, setWfsLayers, networkType }) => {
    const [selectedSections, setSelectedSections] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");

    const layerId = `dionica-${networkType}-layer`;

    const dionicaLayer = useMemo(
        () => wfsLayers.find((layer) => layer.id === layerId),
        [wfsLayers, layerId]
    );

    const features = dionicaLayer?.props?.data;

    const featureArray = useMemo(() => {
        if (Array.isArray(features)) return features;
        if (Array.isArray(features?.features)) return features.features;
        return [];
    }, [features]);

    const uniqueLabels = useMemo(() => {
        const labels = featureArray
            .map((feature) => feature?.properties?.pripadnost_vodu)
            .filter((value) => value !== null && value !== undefined && String(value).trim() !== "");

        return Array.from(new Set(labels.map((value) => String(value).trim()))).sort((a, b) =>
            a.localeCompare(b, "sr", { numeric: true })
        );
    }, [featureArray]);

    const filteredLabels = useMemo(() => {
        const term = normalizeValue(searchTerm);

        if (!term) return uniqueLabels;

        return uniqueLabels.filter((label) =>
            normalizeValue(label).includes(term)
        );
    }, [uniqueLabels, searchTerm]);

    const updateColor = useCallback(
        (nextSelectedSections) => {
            const selectedSet = new Set(nextSelectedSections.map(normalizeValue));
            const selectedKey = nextSelectedSections.map(normalizeValue).join("|");

            setWfsLayers((prevLayers) =>
                prevLayers.map((layer) => {
                    if (layer.id !== layerId) return layer;

                    const originalData = layer.props.data;
                    const isPathLayer = layer instanceof PathLayer || layer.constructor?.name === "PathLayer";

                    const isSelected = (item) => {
                        const vod = getVodValue(item);
                        return selectedSet.has(normalizeValue(vod));
                    };

                    if (isPathLayer) {
                        return new PathLayer({
                            ...layer.props,
                            id: layer.id,
                            data: originalData,

                            getColor: (d) =>
                                isSelected(d) ? SELECTED_COLOR : DEFAULT_COLOR,

                            getWidth: (d) =>
                                isSelected(d) ? 20 : 2,

                            widthUnits: layer.props.widthUnits || "pixels",
                            widthMinPixels: 2,

                            updateTriggers: {
                                ...(layer.props.updateTriggers || {}),
                                getColor: selectedKey,
                                getWidth: selectedKey,
                            },
                        });
                    }

                    return new GeoJsonLayer({
                        ...layer.props,
                        id: layer.id,
                        data: originalData,

                        stroked: true,
                        filled: false,

                        getLineColor: (f) =>
                            isSelected(f) ? SELECTED_COLOR : DEFAULT_COLOR,

                        getLineWidth: (f) =>
                            isSelected(f) ? 20 : 2,

                        lineWidthUnits: layer.props.lineWidthUnits || "pixels",
                        lineWidthMinPixels: 2,

                        updateTriggers: {
                            ...(layer.props.updateTriggers || {}),
                            getLineColor: selectedKey,
                            getLineWidth: selectedKey,
                        },
                    });
                })
            );
        },
        [setWfsLayers, layerId]
    );

    const handleSwitchChange = (section) => {
        setSelectedSections((prevSelected) => {
            const sectionKey = normalizeValue(section);

            const exists = prevSelected.some(
                (item) => normalizeValue(item) === sectionKey
            );

            const nextSelected = exists
                ? prevSelected.filter((item) => normalizeValue(item) !== sectionKey)
                : [...prevSelected, section];

            updateColor(nextSelected);
            return nextSelected;
        });
    };

    const clearSelection = () => {
        setSelectedSections([]);
        updateColor([]);
    };

    return (
        <div className="power-line-div">
            <div className="power-lines-card">
                <div className="power-lines-header">
                    <div className="power-lines-title-wrap">
                        <h3 className="power-lines-title">Dalekovodi</h3>
                        <p className="power-lines-subtitle">
                            Izaberi jedan ili više vodova za isticanje na mapi
                        </p>
                    </div>

                    <div className="power-lines-count">
                        {filteredLabels.length} / {uniqueLabels.length}
                    </div>
                </div>

                <div className="power-lines-toolbar">
                    <div className="power-lines-search-wrap">
                        <input
                            type="text"
                            className="power-lines-search"
                            placeholder="Pretraga po pripadnosti vodu..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <button
                        type="button"
                        className="power-lines-clear-btn"
                        onClick={clearSelection}
                        disabled={selectedSections.length === 0}
                    >
                        Poništi izbor
                    </button>
                </div>

                {selectedSections.length > 0 && (
                    <div className="power-lines-active-selection">
                        Aktivno: <strong>{selectedSections.join(", ")}</strong>
                    </div>
                )}

                <div className="power-lines-list">
                    {filteredLabels.length > 0 ? (
                        filteredLabels.map((label) => (
                            <div className="section-div" key={label}>
                                <div className="section-label-wrap">
                                    <span className="section-label">{label}</span>
                                </div>

                                <label className="switch">
                                    <input
                                        type="checkbox"
                                        checked={selectedSections.some(
                                            (item) => normalizeValue(item) === normalizeValue(label)
                                        )}
                                        onChange={() => handleSwitchChange(label)}
                                    />
                                    <span className="slider2"></span>
                                </label>
                            </div>
                        ))
                    ) : (
                        <div className="power-lines-empty">
                            Nema rezultata za unijetu pretragu.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PowerLines;